import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import MaterialItem from "./CustomerQuoteMaterialItem";
import LaborItem from "./CustomerQuoteLaborItem";
import DiscountItem from "./CustomerQuoteDiscountItem";
import MiscItem from "./CustomerQuoteMiscItem";
import Status from "./CustomerQuoteStatus";
import CustomerTerms from "./CustomerTerms";
import Address from "./Address";
import Contact from "./Contact";
import DocumentRef from "./DocumentRef";
import { QUOTE_SETTINGS } from "./QuoteSettings";
import Total, { computeTotal } from "./QuoteTotal";
import { deepFreeze, isDue, timestampToDate } from "../util/misc";
import { cloneDeep, defaults, isArray, uniq } from "lodash";
import { LOCATION_INFO } from "../constants";
import { scrubCustomerTotal } from "../util/customerQuote.js";

export const BILL_TO_FIELDS = {
  type: "person",
  address: null,
};
deepFreeze(BILL_TO_FIELDS);

export class BillTo extends DataStructure {
  constructor(data = {}) {
    // Backward compatibility
    if (isArray(data)) {
      data = data[0];
    }
    if (!data.address) {
      data = {
        ...BILL_TO_FIELDS,
        address: data,
      };
    }

    super(defaults({}, data, BILL_TO_FIELDS));
  }

  get isValid() {
    return Boolean(this.data.type && this.address.isValid);
  }

  get address() {
    return this.data.address ? new Address(this.data.address) : new Address();
  }

  get name() {
    const contact = this.address.contact;
    return contact ? contact.name : "";
  }
}

export const SHIP_TO_FIELDS = {
  type: "jobsite",
  address: null,
};
deepFreeze(SHIP_TO_FIELDS);

export class ShipTo extends DataStructure {
  constructor(data = {}) {
    super(defaults({}, data, SHIP_TO_FIELDS));
  }

  get isValid() {
    return Boolean(this.data.type && this.address.isValid);
  }

  get address() {
    return this.data.address ? new Address(this.data.address) : new Address();
  }
}

export const CUSTOMER_QUOTE_FIELDS = {
  name: "",
  location: null,
  contact: null,
  deliveryContact: null,
  jobsite: null,
  billTo: null,
  shipTo: null,
  items: [],
  notax: false,
  tax: null,
  terms: null,
  total: null,
  holdScreens: true,
  holdHardware: true,
  attachments: [],
  settings: { ...QUOTE_SETTINGS },
  status: {},
  changes: null,
  salesLeadID: null,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(CUSTOMER_QUOTE_FIELDS);

export default class CustomerQuote extends DataStructure {
  constructor(customerID, id, data = {}) {
    // Backward compatibility
    data = { ...data };
    if (!data.status) {
      data.status = {};
    }
    if (data.sent) {
      if (!data.status.received) {
        data.status.received = data.sent;
      }
    }

    data = sanitize(data, CUSTOMER_QUOTE_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.customerID = customerID;
    this.id = id;

    // Defaults
    const salesLead = this.salesLead;
    this.data.settings = defaults(this.data.settings, QUOTE_SETTINGS);

    // BillTo
    if (salesLead && !this.data.billTo) {
      const contact = salesLead.receivedFrom;
      if (contact && contact.data.addresses.length) {
        this.data.billTo = {
          type: "person",
          address: { ...contact.data.addresses[0], contact: contact.id },
        };
      }
    }
    this.data.billTo = this.billTo.data;

    // ShipTo
    if (salesLead && !this.data.shipTo) {
      this.data.shipTo = {
        type: "jobsite",
        address: cloneDeep(salesLead.data.address),
      };
    }
    this.data.shipTo = this.shipTo.data;

    // Terms
    this.data.terms = this.terms.data;

    // Status
    this.data.status = this.status.data;

    // Total: QuoteTotal's defaults would put back the internal totals
    // toCustomerQuote removed, as zeros
    this.data.total = scrubCustomerTotal(this.total.data);

    // Documents
    if (this.data.attachments) {
      this.data.attachments = this.data.attachments.map(
        (d) => new DocumentRef(d).data
      );
    }

    // Set item lines if not set
    if (this.data.items.length && !this.data.items[0].line) {
      this.setItemLines();
    }
  }

  get expired() {
    return isDue(this.data.status.received.date, -30);
  }

  // Database

  get path() {
    return CustomerQuote.getPath(this.customerID, this.id);
  }

  get responseID() {
    const date = this.data.status.received.date;
    return date ? String(date.getTime()) : null;
  }

  static getPath(customerID, id) {
    let path = `c/${customerID}/quotes`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(quote) {
    return quote.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new CustomerQuote(snapshot.ref.parent.parent.id, snapshot.id, data);
  }

  // Editing

  setItemLines() {
    this.data.items.forEach((item, i) => {
      item.line = i + 1;
    });
  }

  computeTotal(items) {
    const total = computeTotal({
      items,
      tax: this.data.tax,
      payments: this.data.total.payments,
      // Refunds, credit memos, card fees and the early-payment discount are
      // no more quote-derived than the payments above: they are written
      // against the sale. Recomputing the customer's requested-change preview
      // without them dropped the Refund / Credit Memo / Fees rows and left
      // the previewed balance off by their net.
      original: this.data.total,
    });
    if (this.data.total && this.data.total.taxCounty) {
      total.taxCounty = this.data.total.taxCounty;
    }
    return total;
  }

  // Formatting

  get salesLead() {
    return store.state.main.salesLeads[this.salesLeadID];
  }

  get contact() {
    return new Contact(null, this.data.contact);
  }

  get deliveryContact() {
    return this.data.deliveryContact
      ? new Contact(null, this.data.deliveryContact)
      : null;
  }

  get location() {
    return this.data.location ? LOCATION_INFO[this.data.location] : null;
  }

  get billTo() {
    return this.data.billTo ? new BillTo(this.data.billTo) : new BillTo();
  }

  get shipTo() {
    return this.data.shipTo ? new ShipTo(this.data.shipTo) : new ShipTo();
  }

  get jobsite() {
    return this.data.jobsite ? new Address(this.data.jobsite) : new Address();
  }

  get total() {
    return new Total(this.data.total);
  }

  get sortedCategories() {
    const categories = uniq(
      this.data.items.map((item) => item.category || "Other")
    );
    if (categories.length > 1 || categories[0] !== "Other") {
      return categories;
    }
    return [];
  }

  get terms() {
    return new CustomerTerms(this.data.terms);
  }

  get attachments() {
    return this.data.attachments.map((doc) => new DocumentRef(doc));
  }

  get items() {
    return this.data.items.map((item, i) => {
      switch (item.itemType) {
        case "discount":
          return new DiscountItem({ ...item, line: i + 1 });
        case "labor":
          return new LaborItem({ ...item, line: i + 1 });
        case "material":
          return new MaterialItem({ ...item, line: i + 1 });
        case "misc":
        default:
          return new MiscItem({ ...item, line: i + 1 });
      }
    });
  }

  get status() {
    return new Status(this, this.data.status);
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy] || this.data.createdBy
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy] || this.data.modifiedBy
      : null;
  }
}
